/* eslint-disable no-await-in-loop */
import { useState, useEffect, useRef } from 'react';
import { initCommerceApiWithGlobals } from 'common-api';
import type { ProductProps } from 'tsconfig/types';

type ProductCache = {
  [key: string]: ProductProps;
};

const cache: ProductCache = {};
const pendingRequests: Map<string, Promise<ProductProps>> = new Map();

const useProductsData = (productIds: string[] = []) => {
  const productCardAPI: any = initCommerceApiWithGlobals();
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const cacheRef = useRef<ProductCache>(cache);

  useEffect(() => {
    if (productIds.length === 0) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchProducts = async () => {
      const results: ProductProps[] = [];

      for (const productId of productIds) {
        // Use cache if available
        if (cacheRef.current[productId]) {
          results.push(cacheRef.current[productId]);
          continue;
        }

        // Use pending promise if exists
        if (pendingRequests.has(productId)) {
          const existingRequest = pendingRequests.get(productId)!;
          const productData = await existingRequest;
          results.push(productData);
          continue;
        }

        // New request
        const request = productCardAPI.Product.getAllProductData(productId)
          .then((data: ProductProps[]) => {
            const [product] = data;
            cacheRef.current[productId] = product;
            pendingRequests.delete(productId);
            return product;
          })
          .catch(() => {
            pendingRequests.delete(productId);
            return null;
          });

        pendingRequests.set(productId, request);
        const result = await request;
        if (result) {
          results.push(result);
        }
      }

      if (!cancelled) {
        setProducts(results.filter(Boolean));
        setLoading(false);
      }
    };

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, [productIds.join(',')]); // triggers if productIds array content changes

  return { products, loading };
};

export default useProductsData;
